/**
 * La courbe de vues, tracée à la main — SPEC-UI §3.3 (« tracé SVG animé 2 s »),
 * habillage ⟦A-6⟧ : le gros marqueur noir sur du papier, pas une dataviz propre.
 *
 * Les données sont celles du moteur : `video.remaining_curve` est la distribution §6.2 en
 * vues par jour simulé. Rien n'est inventé ici, on dessine ce que la vidéo va rapporter.
 */

import { useEffect, useRef, useState } from "react";

import { useSfx } from "../../components/SoundProvider";

const LARGEUR = 460;
const HAUTEUR = 150;
const MARGE = 14;
const DUREE_MS = 2000;

export function CourbeVues({ courbe, anime = true }: { courbe: readonly number[]; anime?: boolean }) {
  const [avancement, setAvancement] = useState(anime ? 0 : 1);
  const sfx = useSfx();
  const dernierPoint = useRef(0);

  useEffect(() => {
    if (!anime) return;
    let brut = 0;
    const depart = performance.now();
    const boucle = (t: number) => {
      const p = Math.min(1, (t - depart) / DUREE_MS);
      setAvancement(p);
      // Le compteur crante : un tic par point de courbe atteint (§7.6 `views_tick`).
      const point = Math.floor(p * courbe.length);
      if (point > dernierPoint.current) {
        dernierPoint.current = point;
        sfx("views_tick", { volume: 0.5 });
      }
      if (p < 1) brut = requestAnimationFrame(boucle);
    };
    brut = requestAnimationFrame(boucle);
    return () => cancelAnimationFrame(brut);
  }, [anime, courbe, sfx]);

  if (courbe.length === 0) {
    return <p className="vide">Cette vidéo ne rapportera rien. Ça arrive. Ça arrive même souvent.</p>;
  }

  const max = Math.max(...courbe, 1);
  const pas = (LARGEUR - MARGE * 2) / Math.max(1, courbe.length - 1);
  const points = courbe.map((v, i) => {
    const x = MARGE + i * pas;
    const y = HAUTEUR - MARGE - (v / max) * (HAUTEUR - MARGE * 2);
    return [x, y] as const;
  });

  const visibles = Math.max(2, Math.ceil(points.length * avancement));
  const trace = points
    .slice(0, visibles)
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  const aire = `${trace} L${points[visibles - 1]![0].toFixed(1)},${HAUTEUR - MARGE} L${MARGE},${HAUTEUR - MARGE} Z`;

  return (
    <figure className="courbe">
      <svg viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`} role="img" aria-label="Courbe des vues à venir">
        <line
          x1={MARGE}
          y1={HAUTEUR - MARGE}
          x2={LARGEUR - MARGE}
          y2={HAUTEUR - MARGE}
          className="courbe-axe"
        />
        <path d={aire} className="courbe-aire" />
        <path d={trace} className="courbe-trait" />
        {points.slice(0, visibles).map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={3} className="courbe-point" />
        ))}
      </svg>
      {/* ⟦A-8⟧ Pas de légende ici : elle disait « sur N jours », c'est-à-dire exactement la
          durée RELATIVE que l'avenant interdit (« jamais sur 14 jours »). C'est l'écran de
          publication qui la commente, en jours ABSOLUS — et lui seul, pour ne pas afficher
          deux totaux légèrement différents. */}
    </figure>
  );
}
